import React, { useEffect, useState } from "react";
import { db, auth } from "../../firebase";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import Loader from "../../components/Loader";
import ScreenHeader from "../../components/ScreenHeader";
import { useToast } from "../../components/Toast";
import {
  FaCreditCard,
  FaExclamationTriangle,
  FaPhone,
  FaHome,
  FaPen,
} from "react-icons/fa";
import AdminModal from "../../components/AdminModal";
import FormField from "../../components/FormField";
import { tolerantId, fetchUsersByField } from "../../utils/firestoreHelpers";
import { canLogin, computeAccountState, ACCOUNT_STATES } from "../../utils/accountState";
import {
  assignOccupants,
  occupancyAction,
  updateResidentContact,
  nuevoRequestId,
  mensajeDeError,
  codigoDeError,
} from "../../utils/identityApi";
import { useAppDialog } from "../../components/AppDialog";
import ContratoAlquilerCard from "../../components/ContratoAlquilerCard";
import { normKind, esAlquilerDelEdificio, UNIT_KIND_ALQUILER, UNIT_KIND_PORTERIA } from "../../utils/unidadesEdificio";
import { colors, shadows, radius, spacing, typography, cardBase, btnPrimary, btnSecondary, btnGhost, inputBase, pageContainer } from "../../utils/designTokens";

/*
 * Fase 0C de seguridad: el contacto de los ocupantes (updateResidentContact),
 * el tipo de unidad (assignOccupants), la baja y el bloqueo de acceso de una
 * tenencia (occupancyAction) pasan por el servidor. Desde acá solo se escriben
 * los datos físicos de la unidad (la lista que permiten las reglas) y su audit.
 */

// Campos físicos que esta pantalla escribe directo en apartments: los de la
// lista que dejan las reglas 0D. Ocupantes y unitKind van por el servidor.
const CAMPOS_FISICOS = Object.freeze({
  number: "text",
  floor: "text",
  bedroom: "number",
  bathroom: "number",
  m2: "number",
  balcony: "boolean",
  garage: "boolean",
  infront: "text",
});

// Hasta que se publiquen las reglas 0D, el email de login de una cuenta ya
// activada lo cambia solo un superadmin (updateResidentContact, ronda 2).
const MSJ_EMAIL_ACTIVADA =
  "Por ahora, el email de una cuenta ya activada lo cambia un superadmin. Pedíselo a soporte.";

// Activada aunque esté archivada: el servidor bloquea igual su email.
const cuentaActivada = (ficha) =>
  !!ficha && computeAccountState({ ...ficha, archivedAt: null }) === ACCOUNT_STATES.ACTIVE;

// Lugares de la unidad con contacto editable acá (slot de updateResidentContact).
const LUGARES = [
  { slot: "owner", etiqueta: "Propietario", vacio: "No hay propietario asignado a esta unidad." },
  { slot: "tenant", etiqueta: "Inquilino", vacio: "No hay inquilino asignado a esta unidad." },
];

// Confirmaciones que piden los callables (failed-precondition con
// details.requiereConfirmacion o details.motivo): con qué flag se reintenta.
const CONFIRMACIONES = {
  porteria: { flag: "confirmarPorteria", title: "Pasar a portería", confirmLabel: "Pasar a portería", danger: true },
  mes_cerrado: { flag: "confirmarMesCerrado", title: "Mes con cierre", confirmLabel: "Confirmar igual", danger: true },
  telefono_compartido: { flag: "confirmarCompartido", title: "Teléfono compartido", confirmLabel: "Guardar igual", danger: false },
};

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const isValidPhone = (phone) => {
  if (!phone?.trim()) return false;
  if (/[a-zA-Z]/.test(phone)) return false; // letras → invalido
  const digits = phone.trim().replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 13;
};

const isValidEmail = (email) => {
  if (!email?.trim()) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
};

/** Tipo de unidad como lo lee el servidor: "normal", "porteria" o "alquiler_edificio". */
const kindDe = (apt) => normKind(apt);

const MENSAJE_TIPO = {
  normal: "La unidad volvió a ser una unidad normal.",
  [UNIT_KIND_PORTERIA]: "La unidad pasó a portería.",
  [UNIT_KIND_ALQUILER]: "La unidad pasó a Alquiler del edificio. Cargá el contrato en la tarjeta del alquiler.",
};

/** buildingid → clave canónica (10 | "10" | "010" → "10"); lo que no es un id de edificio → null. */
const claveEdificio = (v) => {
  if (typeof v === "number") return Number.isSafeInteger(v) && v >= 0 ? String(v) : null;
  const s = String(v ?? "").trim();
  return /^\d+$/.test(s) && Number.isSafeInteger(Number(s)) ? String(Number(s)) : null;
};

/**
 * ¿El pago suma al saldo de un ocupante en una unidad del edificio `bid`? El
 * mismo criterio que el servidor al bloquear el acceso (identity/unidades.js,
 * pagoDelEdificio): manda su buildingid (si no lo tiene, el buildingId legado)
 * y los pagos viejos sin edificio cuentan. Un pago con el apartmentId de esta
 * unidad cargado desde otro edificio no suma ni traba el bloqueo.
 */
const pagoDelEdificio = (p, bid) => {
  const presente = (v) => v != null && v !== "";
  const crudo = presente(p.buildingid) ? p.buildingid : presente(p.buildingId) ? p.buildingId : null;
  if (crudo == null) return true;
  const clave = claveEdificio(crudo);
  return clave != null && clave === claveEdificio(bid);
};

const formDeUnidad = (data) => ({
  number: data?.number ?? "",
  floor: data?.floor ?? "",
  bedroom: data?.bedroom ?? 0,
  bathroom: data?.bathroom ?? 0,
  m2: data?.m2 ?? 0,
  balcony: !!data?.balcony,
  garage: !!data?.garage,
  infront: data?.infront ?? "",
  unitKind: kindDe(data),
});

/** Datos físicos que cambiaron respecto de la unidad (antes, después y campos). */
const cambiosFisicos = (unidad, formData) => {
  const before = {};
  const after = {};
  const fields = [];
  Object.entries(CAMPOS_FISICOS).forEach(([k, tipo]) => {
    const prevVal = unidad?.[k] ?? (tipo === "boolean" ? false : tipo === "number" ? 0 : "");
    const newVal = formData[k];
    const prevNorm = tipo === "number" ? Number(prevVal) : prevVal;
    const newNorm = tipo === "number" ? Number(newVal) : newVal;
    if (prevNorm !== newNorm) {
      before[k] = prevVal;
      after[k] = newVal;
      fields.push(k);
    }
  });
  return { before, after, fields };
};

// ─── Fichas de los ocupantes ─────────────────────────────────────────────────

/** userid → clave canónica (102 | "102" | "0102" → "102"). */
const claveUserid = (v) => {
  const s = String(v ?? "").trim();
  return /^\d+$/.test(s) ? String(Number(s)) : s;
};

/**
 * Todas las fichas users de esos userids (guardados como string o number),
 * agrupadas por userid canónico. C10 de las reglas 0D: el staff lee una ficha
 * ajena solo si la consulta fija accessBids array-contains el edificio, así
 * que van por fetchUsersByField("userid", variantes, edificio) de
 * firestoreHelpers, que trae TODAS las fichas de cada userid (la elección es
 * de esta pantalla, como el servidor). Si la lectura se niega o falla, queda
 * vacío y la pantalla muestra el userid.
 */
const buscarFichas = async (userids, buildingId) => {
  const claves = [
    ...new Set((userids || []).filter((u) => u != null && u !== "").map(claveUserid).filter(Boolean)),
  ];
  const porUserid = new Map();
  if (!claves.length || buildingId == null || buildingId === "") return porUserid;
  try {
    const fichas = await fetchUsersByField("userid", claves.flatMap((k) => tolerantId(k)), buildingId);
    (fichas || []).forEach(({ id, ...data }) => {
      const k = claveUserid(data.userid);
      if (!porUserid.has(k)) porUserid.set(k, []);
      porUserid.get(k).push({ ...data, docId: id });
    });
  } catch (err) {
    console.warn("[UnidadDetalle] no se pudieron leer las fichas de los ocupantes:", err?.code || err?.message || err);
  }
  return porUserid;
};

const typeuserDe = (u) => (u?.typeuser == null || u.typeuser === "" ? 0 : Number(u.typeuser));

/** Misma ficha que edita updateResidentContact: residente, sin fusionar y no demo. */
const esFichaResidente = (u) =>
  typeuserDe(u) === 0 && u.mergedIntoUserid == null && u.isDemo !== true && String(u.userid) !== "demo_resident";

/**
 * Ficha de un ocupante entre las de su userid: la de residente (la vigente
 * antes que la archivada), igual que la elige el servidor con {apartmentId,
 * slot}. Si no hay ninguna de residente, la primera, solo para mostrar el nombre.
 */
const elegirFicha = (fichas) => {
  const lista = fichas || [];
  const residentes = lista.filter(esFichaResidente);
  return residentes.find((u) => !u.archivedAt) || residentes[0] || lista[0] || null;
};

/** Ficha elegida de cada ocupante del edificio: Map userid canónico → ficha. */
const fichasDeOcupantes = async (userids, buildingId) => {
  const porUserid = await buscarFichas(userids, buildingId);
  const elegidas = new Map();
  porUserid.forEach((fichas, k) => elegidas.set(k, elegirFicha(fichas)));
  return elegidas;
};

/** La ficha de un userid en el mapa de fichasDeOcupantes (o null). */
const fichaDe = (fichas, userid) =>
  userid == null || userid === "" ? null : fichas.get(claveUserid(userid)) || null;

const nombreVisible = (u, fallback = "—") =>
  u?.name || [u?.NombreCompleto, u?.ApellidosCompleto].filter(Boolean).join(" ").trim() || fallback;

/**
 * Nombre y apellidos por separado para el formulario (el servidor arma `name`
 * con los dos). Las fichas viejas sin NombreCompleto tienen solo `name`: va al
 * nombre, sin el apellido si ya está cargado aparte.
 */
const partesNombre = (u) => {
  const apellidos = String(u?.ApellidosCompleto ?? "").trim();
  const nombre = String(u?.NombreCompleto ?? "").trim();
  if (nombre) return { nombre, apellidos };
  const name = String(u?.name ?? "").trim();
  if (apellidos && name.endsWith(apellidos)) return { nombre: name.slice(0, -apellidos.length).trim(), apellidos };
  return { nombre: name, apellidos };
};

const formDeContacto = (u) => ({
  ...partesNombre(u),
  email: String(u?.email ?? ""),
  celular: String(u?.celular ?? ""),
});

const CONTACTO_VACIO = { owner: formDeContacto(null), tenant: formDeContacto(null) };

const errorKey = (slot, campo) => `${slot}_${campo}`;

/**
 * Lo que cambió en una ficha (solo eso se manda). Nombre y apellidos van
 * juntos: así `name` se rearma con lo que se ve en el formulario, también en
 * fichas viejas que solo tenían `name`.
 */
const cambiosDeContacto = (ficha, f) => {
  const antes = formDeContacto(ficha);
  const out = {};
  const nombre = f.nombre.trim();
  const apellidos = f.apellidos.trim();
  if (nombre !== antes.nombre || apellidos !== antes.apellidos) {
    out.nombre = nombre;
    out.apellidos = apellidos;
  }
  const email = f.email.trim();
  if (email.toLowerCase() !== antes.email.trim().toLowerCase()) out.email = email;
  const celular = f.celular.trim();
  if (celular !== antes.celular.trim()) out.celular = celular;
  return out;
};

/** Avisos de la respuesta de updateResidentContact que el staff tiene que ver. */
const avisosDeContacto = (res, etiqueta) => {
  if (!res || res.sinCambios) return [];
  const out = [];
  if (res.authSincronizada) {
    out.push({ tipo: "info", texto: `${etiqueta}: desde ahora inicia sesión con el email nuevo y se cerraron sus sesiones abiertas.` });
  }
  if (res.invitacionRevocada) {
    out.push({ tipo: "warning", texto: `${etiqueta}: con el cambio de email, la invitación anterior ya no sirve. Reenviá la invitación desde Gestión de residentes.` });
  }
  if (res.cuentaExistente) {
    out.push({ tipo: "info", texto: `${etiqueta}: el email nuevo ya tenía cuenta en Habitia; la ficha quedó asociada a esa cuenta.` });
  }
  if (res.avisoOk === false) {
    out.push({ tipo: "warning", texto: `${etiqueta}: no pudimos avisarle del cambio a su email anterior.` });
  }
  return out;
};

// ─── Confirmaciones del servidor ─────────────────────────────────────────────

const etiquetaMeses = (meses) =>
  (Array.isArray(meses) ? meses : [])
    .map((m) => `${MESES[Number(m?.month) - 1] || m?.month} ${m?.year}`)
    .join(", ");

const claveDeConfirmacion = (e) => {
  if (codigoDeError(e) !== "failed-precondition") return null;
  const d = (e && e.details) || {};
  const clave = d.requiereConfirmacion || d.motivo;
  return CONFIRMACIONES[clave] ? clave : null;
};

const textoDeConfirmacion = (clave, e, contexto) => {
  const d = (e && e.details) || {};
  const meses = clave === "mes_cerrado" ? etiquetaMeses(d.meses) : "";
  let texto = mensajeDeError(
    e,
    clave === "mes_cerrado"
      ? `Ese cambio toca meses que ya tienen cierre${meses ? ` (${meses})` : ""}. Confirmá para seguir: el cierre no se recalcula solo.`
      : "Confirmá para seguir."
  );
  // Los meses con cierre se muestran siempre, aunque el mensaje no los nombre.
  if (meses && !texto.includes(meses.split(", ")[0])) texto = `${texto}\n\nMeses con cierre: ${meses}.`;
  return contexto ? `${contexto}: ${texto}` : texto;
};

/**
 * Hoy en Montevideo ("YYYY-MM-DD"). La fecha por defecto no sale de
 * toISOString (UTC): desde las 21 h de Montevideo ya sería mañana.
 */
const hoyMontevideo = () => {
  try {
    const partes = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Montevideo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    const v = (t) => partes.find((p) => p.type === t)?.value;
    if (v("year") && v("month") && v("day")) return `${v("year")}-${v("month")}-${v("day")}`;
  } catch (_) {
    /* sin Intl con zonas horarias: fecha local del navegador */
  }
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const UnidadDetalle = ({ unidadId, setSelected, backTo = "residentes", userType }) => {
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();
  const emailBloqueado = (ficha) => userType !== 2 && cuentaActivada(ficha);
  const [unidad, setUnidad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buildingInfo, setBuildingInfo] = useState(null);

  // === Historial de ocupantes (tenencias) ===
  const [occupancies, setOccupancies] = useState([]);
  const [occupantsInfo, setOccupantsInfo] = useState({}); // userid -> { name, balance }
  const [loadingOccupancies, setLoadingOccupancies] = useState(false);
  const [blockingId, setBlockingId] = useState(null);
  // R5/T1: baja de ocupante sin reemplazo
  const [bajaModal, setBajaModal] = useState(null); // occ a dar de baja
  const [bajaDate, setBajaDate] = useState("");
  const [savingBaja, setSavingBaja] = useState(false);

  // === Fichas (users) del propietario y el inquilino ===
  const [ownerUser, setOwnerUser] = useState(null);
  const [tenantUser, setTenantUser] = useState(null);

  // === Modal Editar Unidad ===
  const [showEdit, setShowEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(formDeUnidad(null));

  // === Modal Editar Contacto ===
  const [showEditContact, setShowEditContact] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [contactForm, setContactForm] = useState(CONTACTO_VACIO);
  const [contactErrors, setContactErrors] = useState({});

  // Fichas del propietario y el inquilino, con el edificio de la unidad (C10).
  const cargarFichasOcupantes = async (apt) => {
    const fichas = await fichasDeOcupantes([apt?.owner, apt?.tenant], apt?.building);
    setOwnerUser(fichaDe(fichas, apt?.owner));
    setTenantUser(fichaDe(fichas, apt?.tenant));
  };

  // Relee la unidad y las fichas de sus ocupantes después de un cambio hecho
  // en el servidor (baja, contacto): lo guardado manda.
  const recargarUnidad = async () => {
    try {
      const snap = await getDoc(doc(db, "apartments", unidadId));
      if (!snap.exists()) return;
      const data = snap.data();
      setUnidad(data);
      await cargarFichasOcupantes(data);
    } catch (err) {
      console.error("Error recargando la unidad:", err);
    }
  };

  /**
   * Llama a un callable y, si el servidor pide una confirmación, la pregunta y
   * reintenta el MISMO payload (mismo requestId: un intento fallido no queda
   * guardado) más el flag. Devuelve null si el usuario cancela.
   */
  const conConfirmaciones = async (llamada, params, contexto = "") => {
    let payload = params;
    for (;;) {
      try {
        return await llamada(payload);
      } catch (e) {
        const clave = claveDeConfirmacion(e);
        const conf = clave ? CONFIRMACIONES[clave] : null;
        if (!conf || payload[conf.flag] === true) throw e;
        const ok = await confirmDialog(textoDeConfirmacion(clave, e, contexto), {
          title: conf.title,
          confirmLabel: conf.confirmLabel,
          danger: conf.danger,
        });
        if (!ok) return null;
        payload = { ...payload, [conf.flag]: true };
      }
    }
  };

  useEffect(() => {
    if (!unidadId) return;
    const fetchData = async () => {
      try {
        const ref = doc(db, "apartments", unidadId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setUnidad(data);
          setFormData(formDeUnidad(data));

          // Fichas del propietario y el inquilino
          await cargarFichasOcupantes(data);

          // Cargar info del edificio
          try {
            if (data?.building) {
              const q = query(collection(db, "building"), where("buildingid", "==", Number(data.building)));
              const snapB = await getDocs(q);
              if (!snapB.empty) {
                const bData = snapB.docs[0].data();
                setBuildingInfo({ name: bData.name, city: bData.city, buildingid: bData.buildingid, customFunds: bData.customFunds || [] });
              } else {
                setBuildingInfo(null);
              }
            } else {
              setBuildingInfo(null);
            }
          } catch (e) {
            console.error("Error al cargar edificio de la unidad:", e);
            setBuildingInfo(null);
          }
        }
      } catch (err) {
        console.error("Error al cargar unidad:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unidadId]);

  // Cargar historial de ocupantes (tenencias) + nombre y saldo pendiente de
  // cada uno en ESTA unidad. El saldo gatea el botón "Bloquear acceso".
  // Espera a tener el edificio de la unidad: la consulta lo fija (C6, reglas 0D).
  const edificioUnidad = unidad?.building;
  const loadOccupancies = async () => {
    if (!unidadId || edificioUnidad == null || edificioUnidad === "") return;
    setLoadingOccupancies(true);
    try {
      // apartmentId + edificio de la unidad ('in' número/string): las reglas 0D
      // solo dejan al staff listar tenencias de su edificio, y así una tenencia
      // con este apartmentId cargada en otro edificio no aparece acá.
      // El id real del doc es el occupancyDocId que recibe el servidor: va
      // después del spread para que un campo `id` guardado no lo pise.
      const occSnap = await getDocs(
        query(
          collection(db, "occupancies"),
          where("apartmentId", "==", String(unidadId)),
          where("buildingid", "in", tolerantId(edificioUnidad))
        )
      );
      const occ = occSnap.docs.map((d) => ({ ...d.data(), id: d.id }));
      setOccupancies(occ);

      // Saldos pendientes por usuario en esta unidad (payments no pagados), solo
      // con los pagos de este edificio (pagoDelEdificio, como el servidor).
      const paySnap = await getDocs(
        query(collection(db, "payments"), where("apartmentId", "==", String(unidadId)))
      );
      const balanceByUser = {};
      paySnap.docs.forEach((d) => {
        const p = d.data();
        if (!pagoDelEdificio(p, edificioUnidad)) return;
        const uid = String(p.userid);
        const pending = p.paid === true
          ? 0
          : (p.remainingAmount != null
              ? Number(p.remainingAmount)
              : Math.max(0, (Number(p.amount) || 0) - (Number(p.paidAmount) || 0)));
        if (pending > 0) balanceByUser[uid] = (balanceByUser[uid] || 0) + pending;
      });

      // Nombres de cada ocupante (una lectura para todos, con el edificio de la
      // unidad: C10). Sin la ficha, el historial muestra el userid.
      const uniqueUserids = [...new Set(occ.map((o) => String(o.userid)))];
      const fichas = await fichasDeOcupantes(uniqueUserids, edificioUnidad);
      const info = {};
      uniqueUserids.forEach((uid) => {
        info[uid] = {
          name: nombreVisible(fichaDe(fichas, uid), `ID ${uid}`),
          balance: balanceByUser[uid] || 0,
        };
      });
      setOccupantsInfo(info);
    } catch (err) {
      console.error("Error cargando historial de ocupantes:", err);
      setOccupancies([]);
      setOccupantsInfo({});
    } finally {
      setLoadingOccupancies(false);
    }
  };

  useEffect(() => {
    loadOccupancies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unidadId, edificioUnidad]);

  // Bloquear el acceso de un ocupante saliente a esta unidad. Solo habilitado
  // cuando su saldo en la unidad es 0 (decisión #4: deuda saldada + OK del
  // admin). El chequeo local es para la UX: el servidor recalcula el saldo y decide.
  const handleBlockAccess = async (occ) => {
    if (blockingId) return;
    const info = occupantsInfo[String(occ.userid)] || {};
    if ((info.balance || 0) > 0) {
      showToast("No se puede bloquear: el ocupante todavía tiene saldo pendiente en esta unidad.", "warning");
      return;
    }
    const ok = await confirmDialog(
      `¿Bloquear el acceso de ${info.name || `ID ${occ.userid}`} a esta unidad? Dejará de ver su histórico y no podrá ingresar a la unidad. Si tiene otras unidades, las seguirá viendo.`,
      { title: "Bloquear acceso a la unidad", confirmLabel: "Bloquear", danger: true }
    );
    if (!ok) return;
    setBlockingId(occ.id);
    try {
      const res = await occupancyAction({
        op: "bloquear_acceso",
        occupancyDocId: occ.id,
        requestId: nuevoRequestId(),
      });
      showToast(res?.sinCambios ? "El acceso ya estaba bloqueado." : "Acceso bloqueado para esta unidad.", "success");
      await loadOccupancies();
    } catch (err) {
      console.error("Error bloqueando acceso:", err);
      const saldo = Number(err?.details?.saldoPendiente);
      showToast(mensajeDeError(err, "No se pudo bloquear el acceso."), saldo > 0 ? "warning" : "error");
      if (saldo > 0) {
        // El servidor ve saldo: se muestra el suyo y el botón queda deshabilitado.
        const uid = String(occ.userid);
        setOccupantsInfo((prev) => ({ ...prev, [uid]: { ...(prev[uid] || {}), balance: saldo } }));
      } else if (["aborted", "failed-precondition", "not-found"].includes(codigoDeError(err))) {
        await loadOccupancies();
      }
    } finally {
      setBlockingId(null);
    }
  };

  // R5/T1: abrir modal de baja (el ocupante se va y no entra reemplazo).
  const openBaja = (occ) => {
    setBajaModal(occ);
    setBajaDate(hoyMontevideo());
  };

  const cerrarBaja = () => {
    if (savingBaja) return;
    setBajaModal(null);
    setBajaDate("");
  };

  const handleBaja = async () => {
    if (savingBaja) return;
    if (!bajaModal || !bajaDate) {
      showToast("Elegí la fecha de baja", "warning");
      return;
    }
    const info = occupantsInfo[String(bajaModal.userid)] || {};
    const ok = await confirmDialog(
      `¿Dar de baja a ${info.name || `ID ${bajaModal.userid}`} el ${bajaDate}? A partir de esa fecha la unidad queda sin ${bajaModal.role === "owner" ? "propietario" : "inquilino"} y el cierre del mes prorratea los gastos comunes (los días ocupados al saliente, el resto al propietario). El saliente conserva acceso de solo lectura a su histórico.`,
      { title: "Dar de baja ocupante", confirmLabel: "Dar de baja", danger: true }
    );
    if (!ok) return;
    setSavingBaja(true);
    try {
      // Si la fecha toca un mes ya cerrado, el servidor pide confirmación.
      const res = await conConfirmaciones(occupancyAction, {
        op: "baja",
        occupancyDocId: bajaModal.id,
        dateTo: bajaDate,
        requestId: nuevoRequestId(),
      });
      if (!res) return; // canceló la confirmación: el modal sigue abierto
      showToast(res.sinCambios ? "Ese ocupante ya estaba dado de baja con esa fecha." : "Ocupante dado de baja.", "success");
      setBajaModal(null);
      setBajaDate("");
      // Refrescar unidad (el lugar quedó libre), fichas e historial.
      await Promise.all([recargarUnidad(), loadOccupancies()]);
    } catch (err) {
      console.error("Error dando de baja ocupante:", err);
      showToast(mensajeDeError(err, "Error al dar de baja."), "error");
      if (["aborted", "not-found"].includes(codigoDeError(err))) {
        await Promise.all([recargarUnidad(), loadOccupancies()]);
      }
    } finally {
      setSavingBaja(false);
    }
  };

  const castValue = (key, value) => {
    const kind = CAMPOS_FISICOS[key];
    if (kind === "number") {
      const n = Number(value);
      return Number.isNaN(n) ? 0 : n;
    }
    if (kind === "boolean") {
      return value === true || value === "true";
    }
    return value;
  };

  const onFieldChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: castValue(key, value) }));
  };

  const abrirEdicion = () => {
    setFormData(formDeUnidad(unidad));
    setShowEdit(true);
  };

  // ===== Guardar Edicion de Unidad =====
  const handleSave = async () => {
    if (saving) return;
    const { before, after, fields } = cambiosFisicos(unidad, formData);
    const kindNuevo = kindDe(formData);
    const cambiaKind = kindNuevo !== kindDe(unidad);
    if (!fields.length && !cambiaKind) {
      setShowEdit(false);
      return;
    }
    // "Alquiler del edificio" no lleva propietario (el dueño es el edificio): si
    // la unidad tiene, se le quita en la misma llamada, avisando antes.
    const propietariosAQuitar = kindNuevo === UNIT_KIND_ALQUILER && cambiaKind
      ? ["owner", "ownerSecondary"].filter((f) => unidad?.[f] != null && unidad[f] !== "")
      : [];
    if (propietariosAQuitar.length) {
      const ok = await confirmDialog(
        "La unidad tiene propietario. Al pasarla a Alquiler del edificio se le quita: el dueño pasa a ser el edificio y esa persona deja de tener acceso por esta unidad. ¿Seguimos?",
        { title: "Pasar a Alquiler del edificio", confirmLabel: "Quitar propietario y seguir", danger: true }
      );
      if (!ok) return;
    }
    setSaving(true);
    let paso = "tipo";
    try {
      // 1) Tipo de unidad: por el servidor, que pide confirmación si la unidad
      //    pasa a portería con ocupantes (pierden el acceso de residente).
      if (cambiaKind) {
        const res = await conConfirmaciones(assignOccupants, {
          apartmentId: unidadId,
          unitKind: kindNuevo,
          ...(propietariosAQuitar.length
            ? { slots: Object.fromEntries(propietariosAQuitar.map((f) => [f, null])) }
            : {}),
          requestId: nuevoRequestId(),
        });
        if (!res) return; // canceló: no se guarda nada y el modal sigue abierto
        setUnidad((prev) => ({
          ...prev,
          unitKind: res.unitKind || kindNuevo,
          ...Object.fromEntries(propietariosAQuitar.map((f) => [f, null])),
        }));
        if (propietariosAQuitar.length) await cargarFichasOcupantes({ ...unidad, owner: null, ownerSecondary: null });
      }

      // 2) Datos físicos: directo, solo los campos que permiten las reglas.
      paso = "datos";
      if (fields.length) {
        await updateDoc(doc(db, "apartments", unidadId), after);

        await addDoc(collection(db, "audit"), {
          action: "edit_unit",
          unitId: unidadId,
          user: auth.currentUser?.email || "desconocido",
          timestamp: serverTimestamp(),
          changes: { before, after, fields },
        });

        setUnidad((prev) => ({ ...prev, ...after }));
      }

      if (cambiaKind) {
        showToast(MENSAJE_TIPO[kindNuevo] || MENSAJE_TIPO.normal, "success");
      }
      setShowEdit(false);
    } catch (err) {
      console.error("Error al guardar cambios:", err);
      showToast(
        paso === "tipo"
          ? mensajeDeError(err, "No se pudo cambiar el tipo de unidad.")
          : "No se pudieron guardar los datos de la unidad.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  // ===== Editar Contacto (updateResidentContact) =====
  const abrirContacto = () => {
    setContactForm({ owner: formDeContacto(ownerUser), tenant: formDeContacto(tenantUser) });
    setContactErrors({});
    setShowEditContact(true);
  };

  const cambiarContacto = (slot, campo, valor) => {
    setContactForm((prev) => ({ ...prev, [slot]: { ...prev[slot], [campo]: valor } }));
    setContactErrors((prev) => ({ ...prev, [errorKey(slot, campo)]: undefined }));
  };

  const handleSaveContact = async () => {
    if (savingContact) return;
    const fichas = { owner: ownerUser, tenant: tenantUser };
    const lugares = LUGARES.filter(({ slot }) => fichas[slot]);

    // Validacion
    const errors = {};
    lugares.forEach(({ slot }) => {
      const f = contactForm[slot];
      if (!f.nombre?.trim()) errors[errorKey(slot, "nombre")] = "Requerido";
      // El email bloqueado no se valida: no se manda y no se puede corregir acá.
      if (!emailBloqueado(fichas[slot]) && !isValidEmail(f.email)) {
        errors[errorKey(slot, "email")] = "Email inválido (ej: nombre@mail.com)";
      }
      if (!isValidPhone(f.celular)) errors[errorKey(slot, "celular")] = "Teléfono inválido (ej: 099123456)";
    });
    if (Object.keys(errors).length > 0) {
      setContactErrors(errors);
      return;
    }
    setContactErrors({});

    // Solo lo que cambió, por ficha (sin el email bloqueado).
    const pedidos = lugares
      .map((l) => {
        const cambios = cambiosDeContacto(fichas[l.slot], contactForm[l.slot]);
        if (emailBloqueado(fichas[l.slot])) delete cambios.email;
        return { ...l, ficha: fichas[l.slot], cambios };
      })
      .filter((p) => Object.keys(p.cambios).length > 0);
    if (!pedidos.length) {
      setShowEditContact(false);
      return;
    }

    setSavingContact(true);
    try {
      // El email de una cuenta activa es el de su login: se confirma antes de guardar nada.
      for (const p of pedidos) {
        if (p.cambios.email === undefined || !canLogin(p.ficha)) continue;
        const ok = await confirmDialog(
          `Cambia el email con el que ${nombreVisible(p.ficha, "esta persona")} inicia sesión en Habitia (pasa a ${p.cambios.email}) y se cierran sus sesiones abiertas. ¿Seguimos?`,
          { title: "Cambiar email de acceso", confirmLabel: "Cambiar email", danger: true }
        );
        if (!ok) return;
      }

      let completo = true;
      const avisos = [];
      for (const p of pedidos) {
        try {
          // Un requestId por persona y por intento; el reintento con
          // confirmarCompartido usa el mismo.
          const res = await conConfirmaciones(
            updateResidentContact,
            {
              buildingId: unidad.building,
              destino: { apartmentId: unidadId, slot: p.slot },
              ...p.cambios,
              requestId: nuevoRequestId(),
            },
            p.etiqueta
          );
          if (!res) {
            completo = false;
            break;
          }
          avisos.push(...avisosDeContacto(res, p.etiqueta));
        } catch (e) {
          console.error(`Error guardando el contacto (${p.slot}):`, e);
          completo = false;
          showToast(`${p.etiqueta}: ${mensajeDeError(e, "No se pudo guardar el contacto.")}`, "error");
        }
      }

      // El servidor normaliza email y celular (y una ficha pudo cambiar en el
      // medio): se releen la unidad y las fichas.
      await recargarUnidad();
      avisos.forEach(({ texto, tipo }) => showToast(texto, tipo));
      if (completo) {
        showToast("Contacto actualizado.", "success");
        setShowEditContact(false);
      }
    } finally {
      setSavingContact(false);
    }
  };

  if (loading) return <Loader />;
  if (!unidad) return <p>No se encontró la unidad.</p>;

  const fichasContacto = { owner: ownerUser, tenant: tenantUser };

  return (
    <div style={styles.page}>
      <ScreenHeader
        icon={FaHome}
        title={`Unidad ${unidad.number || ""}`}
        subtitle={buildingInfo ? `${buildingInfo.name} - ${buildingInfo.city}` : ""}
        onBack={() => setSelected(backTo || "residentes")}
      />

      {/* Contenido en grid */}
      <div style={styles.grid}>
        {/* Datos de la Unidad */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={{ ...styles.iconBadge, background: colors.primaryLighter }}>
              <FaHome size={16} color={colors.primary} />
            </div>
            <h3 style={styles.cardTitle}>Datos de la Unidad</h3>
            <button
              type="button"
              style={styles.iconBtn}
              className="habitia-btn-ghost"
              onClick={abrirEdicion}
              aria-label="Editar unidad"
              title="Editar unidad"
            >
              <FaPen size={14} />
            </button>
          </div>
          <ul style={styles.list}>
            <li>Piso: {unidad.floor}</li>
            <li>Dormitorios: {unidad.bedroom}</li>
            <li>Baños: {unidad.bathroom}</li>
            <li>Garage: {unidad.garage ? "Sí" : "No"}</li>
            <li>Balcón: {unidad.balcony ? "Sí" : "No"}</li>
            <li>Metros²: {unidad.m2}</li>
            <li>Frente: {unidad.infront}</li>
            {buildingInfo && (
              <li>Edificio: {buildingInfo.name} - {buildingInfo.city}</li>
            )}
          </ul>
        </div>

        {/* Saldos Iniciales (migración) */}
        {(unidad.initialBalanceOwner || unidad.initialBalanceOwnerReserva || unidad.initialBalanceTenant) ? (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={{ ...styles.iconBadge, background: colors.primaryLighter }}>
                <FaCreditCard size={16} color={colors.primary} />
              </div>
              <h3 style={styles.cardTitle}>Saldos Iniciales</h3>
            </div>
            <div style={styles.contactGrid}>
              <p style={styles.cardText}><span style={styles.dato}>Propietario - Gastos:</span> ${unidad.initialBalanceOwner || 0}</p>
              <p style={styles.cardText}><span style={styles.dato}>Propietario - Fdo. Reserva:</span> ${unidad.initialBalanceOwnerReserva || 0}</p>
              <p style={styles.cardText}><span style={styles.dato}>Inquilino - Gastos:</span> ${unidad.initialBalanceTenant || 0}</p>
            </div>
            <p style={{ fontSize: typography.sizes.xs, color: colors.gray400, margin: 0 }}>
              Estos saldos generan payments de migración en Cobranza. Editar desde Editar Edificio &gt; Acciones &gt; Saldos Iniciales.
            </p>
          </div>
        ) : null}

        {/* Pagos */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={{ ...styles.iconBadge, background: colors.successLight }}>
              <FaCreditCard size={16} color={colors.success} />
            </div>
            <h3 style={styles.cardTitle}>Pagos de la Unidad</h3>
          </div>
          <p style={styles.cardText}>
            {esAlquilerDelEdificio(unidad) ? "Historial de alquileres y deudas del inquilino." : "Historial de gastos comunes y deudas."}
          </p>
          <button style={styles.btn} onClick={() => setSelected("pagosunidad")}>
            Ver Pagos
          </button>
        </div>

        {/* Contrato de alquiler (unidad del edificio que se alquila) */}
        {esAlquilerDelEdificio(unidad) && (
          <ContratoAlquilerCard
            apartmentId={unidadId}
            apartmentNumber={unidad.number}
            buildingId={unidad.building}
            building={buildingInfo}
          />
        )}

        {/* Incidencias */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={{ ...styles.iconBadge, background: colors.errorLight }}>
              <FaExclamationTriangle size={16} color={colors.error} />
            </div>
            <h3 style={styles.cardTitle}>Incidencias</h3>
          </div>
          <p style={styles.cardText}>Reportes y seguimiento de problemas.</p>
          <button style={styles.btn} onClick={() => setSelected("incidenciasunidad")}>
            Ver Incidencias
          </button>
        </div>

        {/* Contacto */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={{ ...styles.iconBadge, background: colors.gray100 }}>
              <FaPhone size={16} color={colors.gray600} />
            </div>
            <h3 style={styles.cardTitle}>Contacto</h3>
            <button
              type="button"
              style={styles.iconBtn}
              className="habitia-btn-ghost"
              onClick={abrirContacto}
              aria-label="Editar contacto"
              title="Editar contacto"
            >
              <FaPen size={14} />
            </button>
          </div>
          <div style={styles.contactGrid}>
            {esAlquilerDelEdificio(unidad) ? (
              <>
                <p style={styles.cardText}><span style={styles.dato}>Propietario:</span> {buildingInfo?.name || "el edificio"}</p>
                <p style={styles.cardText}><span style={styles.dato}>Tipo:</span> unidad que alquila el edificio (no paga gastos comunes)</p>
              </>
            ) : (
              <>
                <p style={styles.cardText}><span style={styles.dato}>Propietario:</span> {nombreVisible(ownerUser)}</p>
                <p style={styles.cardText}><span style={styles.dato}>Email:</span> {ownerUser?.email || "—"}</p>
                <p style={styles.cardText}><span style={styles.dato}>Teléfono:</span> {ownerUser?.celular || "—"}</p>
              </>
            )}
            <p style={styles.cardText}><span style={styles.dato}>Inquilino:</span> {nombreVisible(tenantUser)}</p>
            <p style={styles.cardText}><span style={styles.dato}>Email:</span> {tenantUser?.email || "—"}</p>
            <p style={styles.cardText}><span style={styles.dato}>Teléfono:</span> {tenantUser?.celular || "—"}</p>
          </div>
        </div>
      </div>

      {/* Historial de ocupantes (tenencias) */}
      <div style={{ ...styles.card, marginTop: spacing[4] }}>
        <div style={styles.cardHeader}>
          <FaHome color={colors.primary} />
          <h3 style={styles.cardTitle}>Historial de ocupantes</h3>
        </div>
        {loadingOccupancies ? (
          <p style={styles.cardText}>Cargando historial…</p>
        ) : occupancies.length === 0 ? (
          <p style={{ ...styles.cardText, color: colors.gray400 }}>
            Esta unidad todavía no tiene tenencias registradas con fecha. Se registran al asignar
            ocupantes con “fecha de ingreso”.
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: typography.sizes.sm }}>
              <thead>
                <tr style={{ textAlign: "left", color: colors.gray600 }}>
                  <th style={styles.thCell}>Ocupante</th>
                  <th style={styles.thCell}>Rol</th>
                  <th style={styles.thCell}>Período</th>
                  <th style={styles.thCell}>Estado</th>
                  <th style={styles.thCell}>Saldo en la unidad</th>
                  <th style={styles.thCell}></th>
                </tr>
              </thead>
              <tbody>
                {[...occupancies]
                  .sort((a, b) => {
                    // Vigente (sin fecha de fin) primero.
                    const aVig = a.active !== false && !a.dateTo;
                    const bVig = b.active !== false && !b.dateTo;
                    if (aVig !== bVig) return aVig ? -1 : 1;
                    // Luego, del más reciente al más antiguo: por fecha de fin
                    // desc y, a igualdad, por fecha de inicio desc.
                    const aTo = a.dateTo || "9999-12-31";
                    const bTo = b.dateTo || "9999-12-31";
                    if (aTo !== bTo) return bTo.localeCompare(aTo);
                    return String(b.dateFrom || "").localeCompare(String(a.dateFrom || ""));
                  })
                  .map((occ) => {
                  const info = occupantsInfo[String(occ.userid)] || {};
                  const vigente = occ.active !== false && !occ.dateTo;
                  const blocked = occ.accessBlocked === true;
                  const balance = info.balance || 0;
                  const estado = blocked
                    ? "Acceso bloqueado"
                    : vigente
                    ? "Vigente"
                    : "Finalizada";
                  const estadoColor = blocked ? colors.error : vigente ? colors.success : colors.gray400;
                  const canBlock = !vigente && !blocked && balance <= 0;
                  return (
                    <tr key={occ.id} style={{ borderTop: `1px solid ${colors.gray200}` }}>
                      <td style={styles.tdCell}>{info.name || `ID ${occ.userid}`}</td>
                      <td style={styles.tdCell}>{occ.role === "owner" ? "Propietario" : "Inquilino"}</td>
                      <td style={styles.tdCell}>
                        {formatOccDate(occ.dateFrom)} → {occ.dateTo ? formatOccDate(occ.dateTo) : "vigente"}
                      </td>
                      <td style={{ ...styles.tdCell, color: estadoColor }}>{estado}</td>
                      <td style={styles.tdCell}>
                        {balance > 0 ? `$ ${balance.toLocaleString("es-UY")}` : "—"}
                      </td>
                      <td style={styles.tdCell}>
                        {vigente && !blocked && occ.role !== "owner" && (
                          <button
                            style={{
                              ...styles.btnSecondary,
                              padding: `${spacing[1]}px ${spacing[2]}px`,
                              fontSize: typography.sizes.xs,
                            }}
                            onClick={() => openBaja(occ)}
                            title="Dar de baja a este ocupante (se va y no entra reemplazo)"
                          >
                            Dar de baja
                          </button>
                        )}
                        {vigente && !blocked && occ.role === "owner" && (
                          <span
                            style={{ fontSize: typography.sizes.xs, color: colors.gray400 }}
                            title="Toda unidad debe tener un propietario. Para cambiarlo, invitá al nuevo como Propietario y reemplaza al actual."
                          >
                            Cambiar por “Invitar”
                          </span>
                        )}
                        {!vigente && !blocked && (
                          <button
                            style={{
                              ...styles.btnSecondary,
                              padding: `${spacing[1]}px ${spacing[2]}px`,
                              fontSize: typography.sizes.xs,
                              opacity: canBlock && blockingId !== occ.id ? 1 : 0.5,
                              cursor: canBlock && blockingId !== occ.id ? "pointer" : "not-allowed",
                            }}
                            disabled={!canBlock || blockingId === occ.id}
                            onClick={() => handleBlockAccess(occ)}
                            title={
                              canBlock
                                ? "Bloquear el acceso de este saliente a la unidad"
                                : "Solo se puede bloquear cuando el saldo en la unidad es 0"
                            }
                          >
                            {blockingId === occ.id ? "Bloqueando…" : "Bloquear acceso"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* R5/T1: Modal Dar de baja ocupante */}
      <AdminModal
        open={!!bajaModal}
        onClose={cerrarBaja}
        title="Dar de baja ocupante"
        size="narrow"
        footer={
          <>
            <button type="button" style={styles.btnSecondary} className="habitia-btn-secondary" onClick={cerrarBaja} disabled={savingBaja}>Cancelar</button>
            <button type="button" style={{ ...styles.btnPrimary, opacity: savingBaja ? 0.7 : 1 }} className="habitia-btn-primary" onClick={handleBaja} disabled={savingBaja}>
              {savingBaja ? "Procesando…" : "Dar de baja"}
            </button>
          </>
        }
      >
        {bajaModal && (
          <div>
            <p style={{ fontSize: typography.sizes.base, color: colors.gray900, marginBottom: spacing[2] }}>
              {occupantsInfo[String(bajaModal.userid)]?.name || `ID ${bajaModal.userid}`} — {bajaModal.role === "owner" ? "Propietario" : "Inquilino"}
            </p>
            <FormField label="Último día de responsabilidad" htmlFor="unidad-baja-fecha" required>
              <input
                id="unidad-baja-fecha"
                type="date"
                className="habitia-input"
                style={styles.input}
                value={bajaDate}
                min={bajaModal.dateFrom || undefined}
                onChange={(e) => setBajaDate(e.target.value)}
                disabled={savingBaja}
              />
            </FormField>
            <p style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginTop: spacing[2], lineHeight: 1.5 }}>
              Desde el día siguiente, la unidad queda sin este ocupante. El cierre del mes prorratea los gastos comunes por días. El saliente conserva acceso de solo lectura.
            </p>
          </div>
        )}
      </AdminModal>

      {/* Modal Edicion Unidad */}
      <AdminModal
        open={showEdit}
        onClose={() => { if (!saving) setShowEdit(false); }}
        title="Editar Unidad"
        size="wide"
        footer={
          <>
            <button type="button" style={styles.btnSecondary} className="habitia-btn-secondary" onClick={() => setShowEdit(false)} disabled={saving}>
              Cancelar
            </button>
            <button type="button" style={{ ...styles.btnPrimary, opacity: saving ? 0.7 : 1 }} className="habitia-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </>
        }
      >
        <div style={styles.modalBody}>
          <FormField label="Número" htmlFor="unidad-number">
            <input
              id="unidad-number"
              className="habitia-input"
              style={styles.input}
              value={formData.number}
              onChange={(e) => onFieldChange("number", e.target.value)}
            />
          </FormField>
          <FormField label="Piso" htmlFor="unidad-floor">
            <input
              id="unidad-floor"
              className="habitia-input"
              style={styles.input}
              value={formData.floor}
              onChange={(e) => onFieldChange("floor", e.target.value)}
            />
          </FormField>

          <FormField label="Dormitorios" htmlFor="unidad-bedroom">
            <input
              id="unidad-bedroom"
              type="number"
              className="habitia-input"
              style={styles.input}
              value={formData.bedroom}
              onChange={(e) => onFieldChange("bedroom", e.target.value)}
              min={0}
            />
          </FormField>
          <FormField label="Baños" htmlFor="unidad-bathroom">
            <input
              id="unidad-bathroom"
              type="number"
              className="habitia-input"
              style={styles.input}
              value={formData.bathroom}
              onChange={(e) => onFieldChange("bathroom", e.target.value)}
              min={0}
            />
          </FormField>

          <FormField label="Metros²" htmlFor="unidad-m2">
            <input
              id="unidad-m2"
              type="number"
              className="habitia-input"
              style={styles.input}
              value={formData.m2}
              onChange={(e) => onFieldChange("m2", e.target.value)}
              min={0}
            />
          </FormField>
          <FormField label="Frente" htmlFor="unidad-infront">
            <input
              id="unidad-infront"
              className="habitia-input"
              style={styles.input}
              value={formData.infront}
              onChange={(e) => onFieldChange("infront", e.target.value)}
            />
          </FormField>

          <FormField label="Balcón" htmlFor="unidad-balcony">
            <select
              id="unidad-balcony"
              className="habitia-input"
              style={styles.input}
              value={formData.balcony ? "true" : "false"}
              onChange={(e) => onFieldChange("balcony", e.target.value)}
            >
              <option value="true">Sí</option>
              <option value="false">No</option>
            </select>
          </FormField>
          <FormField label="Garage" htmlFor="unidad-garage">
            <select
              id="unidad-garage"
              className="habitia-input"
              style={styles.input}
              value={formData.garage ? "true" : "false"}
              onChange={(e) => onFieldChange("garage", e.target.value)}
            >
              <option value="true">Sí</option>
              <option value="false">No</option>
            </select>
          </FormField>

          <FormField label="Tipo de unidad" htmlFor="unidad-unitkind">
            <select
              id="unidad-unitkind"
              className="habitia-input"
              style={styles.input}
              value={formData.unitKind || "normal"}
              onChange={(e) => onFieldChange("unitKind", e.target.value)}
            >
              <option value="normal">Normal</option>
              <option value="porteria">Portería (no paga gastos comunes)</option>
              <option value={UNIT_KIND_ALQUILER}>Alquiler del edificio</option>
            </select>
          </FormField>
          {formData.unitKind === UNIT_KIND_ALQUILER && (
            <p style={{ gridColumn: "1 / -1", margin: 0, fontSize: typography.sizes.sm, color: colors.gray600, lineHeight: 1.5 }}>
              No paga gastos comunes ni fondos; el inquilino sí entra a la plataforma y paga el alquiler por ahí. El dueño es el edificio: la unidad no lleva propietario y su alícuota es 0.
            </p>
          )}

          <div style={{ gridColumn: "1 / -1", background: colors.gray50, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, padding: `${spacing[3]}px ${spacing[4]}px`, fontSize: typography.sizes.sm, color: colors.gray600, lineHeight: 1.5 }}>
            <span style={styles.dato}>Ocupantes:</span> {esAlquilerDelEdificio(unidad) ? `Propietario: ${buildingInfo?.name || "el edificio"}` : `Propietario ID ${unidad?.owner || "—"}`}
            {unidad?.tenant ? ` · Inquilino ID ${unidad.tenant}` : " · sin inquilino"}.
            <div style={{ marginTop: spacing[1] }}>
              Para cambiar ocupantes usá <em>Invitar residente</em> (alta) o <em>Dar de baja</em> en el historial de ocupantes. Editar el ID acá dejaba tenencias inconsistentes, por eso se deshabilitó.
            </div>
          </div>

        </div>
      </AdminModal>

      {/* Modal Edicion Contacto */}
      <AdminModal
        open={showEditContact}
        onClose={() => { if (!savingContact) setShowEditContact(false); }}
        title="Editar Contacto"
        size="wide"
        footer={
          <>
            <button type="button" style={styles.btnSecondary} className="habitia-btn-secondary" onClick={() => setShowEditContact(false)} disabled={savingContact}>
              Cancelar
            </button>
            <button
              type="button"
              style={{ ...styles.btnPrimary, opacity: savingContact ? 0.7 : 1 }}
              className="habitia-btn-primary"
              onClick={handleSaveContact}
              disabled={savingContact}
            >
              {savingContact ? "Guardando..." : "Guardar"}
            </button>
          </>
        }
      >
        <div style={styles.contactModalBody}>
          {LUGARES.map(({ slot, etiqueta, vacio }, i) => {
            const ficha = fichasContacto[slot];
            const seccion = { gridColumn: "1 / -1", marginTop: i > 0 ? spacing[2] : 0 };
            if (!ficha) {
              return (
                <div key={slot} style={seccion}>
                  <p style={styles.vacioModal}>{vacio}</p>
                </div>
              );
            }
            const f = contactForm[slot];
            const id = (campo) => `unidad-contacto-${slot}-${campo}`;
            const err = (campo) => contactErrors[errorKey(slot, campo)];
            return (
              <React.Fragment key={slot}>
                <div style={seccion}>
                  <p style={styles.seccionModal}>{etiqueta}</p>
                </div>
                <FormField label="Nombre" htmlFor={id("nombre")} required error={err("nombre")}>
                  <input
                    id={id("nombre")}
                    className="habitia-input"
                    style={estiloInput(err("nombre"))}
                    value={f.nombre}
                    maxLength={80}
                    autoComplete="off"
                    onChange={(e) => cambiarContacto(slot, "nombre", e.target.value)}
                  />
                </FormField>
                <FormField label="Apellidos" htmlFor={id("apellidos")}>
                  <input
                    id={id("apellidos")}
                    className="habitia-input"
                    style={styles.input}
                    value={f.apellidos}
                    maxLength={80}
                    autoComplete="off"
                    onChange={(e) => cambiarContacto(slot, "apellidos", e.target.value)}
                  />
                </FormField>
                <FormField
                  label="Email"
                  htmlFor={id("email")}
                  required
                  error={err("email")}
                  hint={
                    emailBloqueado(ficha)
                      ? MSJ_EMAIL_ACTIVADA
                      : canLogin(ficha)
                        ? "Es el email con el que inicia sesión en Habitia."
                        : undefined
                  }
                >
                  <input
                    id={id("email")}
                    type="email"
                    className="habitia-input"
                    style={estiloInput(err("email"))}
                    value={f.email}
                    autoComplete="off"
                    disabled={emailBloqueado(ficha)}
                    onChange={(e) => cambiarContacto(slot, "email", e.target.value)}
                  />
                </FormField>
                <FormField label="Teléfono" htmlFor={id("celular")} required error={err("celular")}>
                  <input
                    id={id("celular")}
                    type="tel"
                    inputMode="tel"
                    className="habitia-input"
                    style={estiloInput(err("celular"))}
                    value={f.celular}
                    placeholder="099123456"
                    autoComplete="off"
                    onChange={(e) => cambiarContacto(slot, "celular", e.target.value)}
                  />
                </FormField>
              </React.Fragment>
            );
          })}
        </div>
      </AdminModal>
    </div>
  );
};

// ─── Estilos con design tokens ───────────────────────────────────────────────
const styles = {
  page: {
    ...pageContainer,
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: spacing[4],
  },
  card: {
    ...cardBase,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  cardHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: 4 },
  iconBadge: {
    width: 36,
    height: 36,
    borderRadius: radius.sm,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  // Lápiz de editar: botón de solo icono (con aria-label).
  iconBtn: {
    ...btnGhost,
    marginLeft: "auto",
    color: colors.gray400,
  },
  cardTitle: { margin: 0, fontSize: typography.sizes.lg, fontWeight: typography.weights.normal, color: colors.black },
  cardText: { fontSize: typography.sizes.base, margin: 0, color: colors.gray600 },
  // Etiqueta de un dato ("Propietario:"): jerarquía por color, sin negrita.
  dato: { color: colors.black },
  contactGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 6,
  },
  btn: {
    ...btnPrimary,
    marginTop: "auto",
  },
  list: { listStyle: "none", padding: 0, fontSize: typography.sizes.base, color: colors.gray900, lineHeight: "24px" },

  // ===== Modal (grid layout para campos) =====
  modalBody: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: spacing[3],
  },
  // Contacto: dos columnas (nombre | apellidos, email | teléfono); una en mobile.
  contactModalBody: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
    gap: spacing[3],
  },
  seccionModal: { margin: `0 0 ${spacing[1]}px`, fontSize: typography.sizes.base, fontWeight: typography.weights.normal, color: colors.black },
  vacioModal: { margin: 0, fontSize: typography.sizes.base, color: colors.gray600 },
  input: {
    ...inputBase,
    height: 36,
    padding: "6px 10px",
  },
  btnSecondary: {
    ...btnSecondary,
  },
  btnPrimary: {
    ...btnPrimary,
    boxShadow: shadows.md,
  },
  thCell: { padding: "6px 8px", fontWeight: typography.weights.normal, whiteSpace: "nowrap" },
  tdCell: { padding: "8px 8px", color: colors.gray900, verticalAlign: "middle" },
};

// Input con borde de error (border completo: no se mezcla con borderColor).
function estiloInput(error) {
  return { ...styles.input, border: `1px solid ${error ? colors.error : colors.gray200}` };
}

// Formatea "YYYY-MM-DD" como DD/MM/YYYY para el historial de ocupantes.
function formatOccDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return String(iso);
  return `${d}/${m}/${y}`;
}

export default UnidadDetalle;
